<template>
    <div v-if="isLoading">
        <font-awesome-icon icon="spinner" spin/>
    </div>
    <div v-else>

        <form>
            <div class="row mb-4">
                <b-col cols="3">
                    <label>Toon alle producten in levering</label><br/>
                    <b-checkbox switch value="1" unchecked-value="0" v-model="config.showAllProducts" size="lg"/>
                </b-col>
                <b-col cols="2">
                    <label>Sorteervolgorde</label><br/>
                    <select class="form-control form-control-sm" v-model="config.sortOrder">
                        <option value="desc">Nieuwste eerst</option>
                        <option value="asc">Oudste eerst</option>
                    </select>
                </b-col>
            </div>
        </form>

        <b-card
            v-for="dispatchRequest in dispatchRequestsFiltered" :key="dispatchRequest.id"
            class="dispatchRequest mb-4 mt-1 font-size-md"
            no-body
            :data-id="dispatchRequest.id"
        >
            <b-card-header
                :header-bg-variant="dispatchRequest.is_replaced ? 'light' : ''"
                :class="{'bg-midnight-bloom-light': !dispatchRequest.is_replaced}"
                :data-drq-id="dispatchRequest.id"
            >
                <b-col cols="12" md="3" class="px-0">
                    <h6 class="mb-0">
                        <span :title="dispatchRequest.status"><dispatch-request-icon :status="dispatchRequest.status" class="mr-2"/></span>
                        <strong class="mr-2">{{ dispatchRequestStatus(dispatchRequest.status, dispatchRequest.dispatch_type?.identifier) | uppercase }}</strong>
                        <small v-if="dispatchRequest.status_reason" class="mr-2">
                            <font-awesome-icon icon="circle-info" :title="dispatchRequest.status_reason" class="ml-2 text-info align-text-top"/>
                        </small>
                    </h6>
                </b-col>
                <b-col cols="12" md="3" class="px-0">
                    <h5 class="mb-0" v-if="showDispatchRequestDeliveryFields(dispatchRequest)">
                        <small>
                            <strong>Uitleverdatum: </strong>
                            <span v-if="dispatchRequest.dispatch_date">{{ dispatchRequest.dispatch_date | date }}</span>
                            <span v-else>onbekend</span>
                        </small>
                    </h5>
                </b-col>
                <b-col cols="11" md="5">
                    <b-row>
                        <b-col>
                            <h6 class="mb-0"><small><strong>{{ $t('omt.gui.orders.label.location') }}:</strong> {{ dispatchRequest.dispatcher_type | dispatcherType }} - {{ getDispatcherName(dispatchRequest) }}
                                ({{ dispatchRequest.dispatch_type.display_name }})</small>
                            </h6>
                            <p class="text-truncate mb-0">
                                <small v-if="dispatchRequest.responsible_location" v-b-tooltip.hover :title="`${$t('omt.gui.orders.label.responsible-location')}: ${dispatchRequest.responsible_location.display_name}`">
                                    <strong>{{ $t('omt.gui.orders.label.responsible-location') }}:</strong>
                                    {{ dispatchRequest.responsible_location?.location_type.display_name }} -
                                    <span v-if="!hasAnyPermission(['locations.services.view', 'locations.services.edit'])">{{ dispatchRequest.responsible_location.display_name }}</span>
                                    <b-link v-else :to="{
                                          name: 'EditLocationServices',
                                          params: {
                                            locationTypeIdentifier: dispatchRequest.responsible_location?.location_type.identifier,
                                            locationIdentifier: dispatchRequest.responsible_location?.identifier,
                                          }}" style="color:inherit;"
                                            title="Bekijk locatie"
                                    >{{ dispatchRequest.responsible_location.display_name }}</b-link>
                                </small>
                            </p>
                        </b-col>
                    </b-row>
                </b-col>
                <b-col cols="1" class="text-right">
                    <font-awesome-icon v-if="!dispatchRequest.is_replaced" icon="star-of-life" class="text-info font-size-lg"
                                       title="Huidige status"
                    />
                </b-col>
            </b-card-header>
            <b-card-body class="pl-4 pb-3 pt-4" body-bg-variant="default">
                <b-table-simple stacked="sm" small borderless class="mb-0">
                    <colgroup>
                        <col style="width:110px;"/>
                        <col style="width:140px;"/>
                        <col style="width:auto;"/>
                        <col style="width:210px;"/>
                    </colgroup>

                    <b-thead>
                        <b-tr>
                            <b-th>Aantal</b-th>
                            <b-th>Artikel nr</b-th>
                            <b-th>Naam</b-th>
                            <b-th>Extra informatie</b-th>
                        </b-tr>
                    </b-thead>

                    <b-tbody>
                        <b-tr v-for="line in dispatchRequest.dispatch_request_lines" :key="line.id"
                              v-if="config.showAllProducts === '1' || line.product_identifier === dispatchRequestLine.product_identifier"
                              :class="{
                                'opacity-5': line.product_identifier !== dispatchRequestLine.product_identifier,
                              }"
                        >
                            <b-td stacked-heading="Aantal">
                                {{ line.quantity }}
                            </b-td>
                            <b-td stacked-heading="Artikel nr">
                                {{ line.product_identifier }}
                            </b-td>
                            <b-td stacked-heading="Naam">{{ line.product_name ?? getSalesOrderLine(line.sales_order_line_id).product_name }}</b-td>
                            <b-td stacked-heading="Extra informatie">
                                <small v-if="dispatchRequest.storage_location_id">
                                    <i>Gescand: {{ line.final_scanned_quantity }}</i>
                                </small>

                                <p v-if="getSalesOrderLine(line.sales_order_line_id)?.promotions?.length" class="m-0">
                                    <small style="text-decoration: underline dotted;cursor:default" class="text-primary" :id="`promotions_${line.id}`">
                                        {{ getSalesOrderLine(line.sales_order_line_id).promotions.length }} promotie<span v-if="getSalesOrderLine(line.sales_order_line_id).promotions.length > 1">s</span>
                                    </small>

                                    <b-popover :target="`promotions_${line.id}`" triggers="hover" placement="auto" no-fade delay="0" custom-class="promotion-popover" :key="line.id">
                                        <promotion-lines :promotion-lines="getSalesOrderLine(line.sales_order_line_id).promotions" style="background: white;"/>
                                    </b-popover>
                                </p>

                                <small v-if="dispatchRequest.status.startsWith('return-')">
                                    <strong>Retourreden:</strong> {{ getReturnReason(line.return_reason_id).display_name ?? 'onbekend' }}<br />
                                </small>
                                <small v-if="dispatchRequest.status === 'return-received'">
                                    <strong>Staat:</strong> {{ getProductState(line.product_state_id).display_name ?? 'onbekend'}}<br />
                                </small>
                            </b-td>
                        </b-tr>
                    </b-tbody>

                </b-table-simple>
            </b-card-body>
            <b-card-footer footer-bg-variant="light" footer-border-variant="light">
                <b-col cols="3" class="pr-4 pl-0 text-black-50">
                    <div v-if="showDispatchRequestDeliveryFields(dispatchRequest)">
                        <carrier-icon v-if="dispatchRequest.carrier" :carrier="dispatchRequest.carrier.identifier" class="carrier-icon d-inline-block align-middle"/>
                        <span class="d-inline-block ml-2">{{ dispatchRequest.carrier ? dispatchRequest.carrier.display_name : '' }}</span>
                        &rarr; {{ dispatchRequest.carrier_method ? dispatchRequest.carrier_method.display_name : 'Vervoersmethode onbekend' }}
                    </div>
                </b-col>
                <b-col cols="3" class="pr-4 text-black-50">
                    <span v-if="dispatchRequest.tracking_codes.length">
                        Track &amp; trace:
                        <a v-for="(trackingCode, index) in dispatchRequest.tracking_codes" :href="trackingCode.url" class="text-info d-inline-block mx-1" target="_blank">[{{ index + 1 }}]</a>
                    </span>
                </b-col>
                <b-col cols="3" class="pr-4">
                    <span class="text-black-50">Laatste update:</span> {{ dispatchRequest.updated_at | datetime }}
                </b-col>
                <b-col cols="3" class="text-black-50">
                    <span v-if="dispatchRequest.dispatch_request_batch_id && hasPermission('orders.batch.process')">
                        <strong>In batch: </strong>
                        <b-link :to="{name: 'ShipmentBatchView',
                                  params: {
                                    locationTypeIdentifier: dispatchRequest.dispatcher_type,
                                    locationIdentifier: dispatchRequest.dispatcher_identifier,
                                    batchIdentifier: dispatchRequest.dispatch_request_batch?.identifier,
                                  }}"
                        >
                            {{dispatchRequest.dispatch_request_batch?.identifier}}
                        </b-link>
                        <br />
                    </span>
                    <span v-if="dispatchRequest.storage_location_id">
                        <strong>In sorteerzone:</strong> {{dispatchRequest.storage_location?.storage_zone.display_name}} &rarr; {{dispatchRequest.storage_location?.display_name}}
                    </span>
                </b-col>
            </b-card-footer>
        </b-card>
    </div>
</template>

<script>
import DispatchRequestIcon from "../../../components/DispatchRequestIcon.vue";
import CarrierIcon from "../../../components/CarrierIcon.vue";
import PromotionLines from "./PromotionLines.vue";
import GenericHelpers from "../../../helpers/generic-helpers";
import * as DispatchHelper from "../../../helpers/dispatch";
import {DispatchRequestsBase} from "./dispatchRequestsBase";
import _ from "lodash";
import {dispatchRequestStatus} from "../../../helpers/dispatch";

const LOCALSTORAGE_KEY_CONFIG = 'drqHistory.config'

export default {
    extends: DispatchRequestsBase,
    components: {PromotionLines, CarrierIcon, DispatchRequestIcon},

    props: {
        salesOrder: {required: true},
        dispatchRequestLine: {required: true},
    },

    data() {
        return {
            isLoading: true,
            dispatchRequests: [],
            relevantLocations: [],
            config: {
                showAllProducts: '0',
                sortOrder: 'asc',
            },
        }
    },

    mounted() {
        const cachedConfig = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY_CONFIG)) ?? {}
        if (!_.difference(Object.keys(cachedConfig), Object.keys(this.config)).length) {
            this.config = cachedConfig
        }

        this.$http.get(`/api/rest/sales-orders/${this.salesOrder.identifier}/dispatch-requests`, {
            params: {
                include: [
                    'carrier_method',
                    'tracking_codes',
                    'dispatch_type',
                    'dispatch_request_lines',
                    'dispatch_request_batch',
                    'responsible_location.location_type',
                ].join(';')
            },
        })
            .then(response => {
                this.dispatchRequests = response.data

                const query = GenericHelpers.toQueryString({
                    type_and_identifier: this.dispatchRequests.map(dr => {
                        return `${DispatchHelper.dispatcherType(dr.dispatcher_type).id},${dr.dispatcher_identifier}`
                    }),
                })

                this.$http.get(`/api/rest/locations?${query}`)
                    .then(response => {
                        this.relevantLocations = response.data
                        this.isLoading = false
                    })
            })
            .catch(e => {
                this.$alert("Fout bij het inladen van de leveringsstatus-geschiedenis", 'danger')
            })
    },

    computed: {
        dispatchRequestsFiltered() {
            return _.chain(this.dispatchRequests)
                .filter(drq => {
                    return drq.dispatch_request_lines.some(line => line.product_identifier === this.dispatchRequestLine.product_identifier)
                })
                .orderBy('id', this.config.sortOrder)
                .value()
        },
    },

    methods: {
        dispatchRequestStatus

    },

    watch: {
        config: {
            deep: true,
            handler(newVal) {
                localStorage.setItem(LOCALSTORAGE_KEY_CONFIG, JSON.stringify(newVal))
            },
        }
    },


}
</script>

<style scoped>

</style>
